import type { ProductData } from '@appcharge/shared-ui';
import { OfferUI } from 'common/contracts';
import {
  EFeatureFlag,
  EProgressBarType,
  ESpecialOfferInternalViewModel
} from 'constants/enums';
import { AnyObjectSchema } from 'yup';

import { GradientDirection } from 'components/AcGradientInput/AcGraidentInput.types';
import { GeneralThemeFormValues } from 'components/Theme/GeneralSettings/generalTheme.types';
import { StoreThemeFormValues } from 'components/Theme/StoreTheme/storeTheme.types';

export type UniqueFlag = 'isNameUnique' | 'isExternalIdUnique';

export enum OfferDesignTypes {
  SpecialOffer = 'SpecialOffer',
  Bundle = 'Bundle',
  PopUp = 'PopUp',
  TriggeredPopup = 'TriggeredPopup',
  RollingOffer = 'RollingOffer',
  BuyOneGetMany = 'OnePlusMany',
  ProgressBar = 'ProgressBar',
  RewardCalendar = 'RewardCalendar'
}

export interface OfferDesignTypeItem {
  content: string;
  key: OfferDesignTypes;
  value: OfferDesignTypes;
}

export interface OfferDesignRow {
  _id: string;
  name: string;
  externalId: string;
  backgroundImage?: string;
  offerUiType: string;
  offerUiSubType?: string;
  createdAt: string;
  rewardCalendar?: {
    headerImage?: string;
    mainRewardBackgroundImage?: string;
    subRewardBackgroundImage?: string;
  };
}

export interface OfferDesignActions {
  onEdit: (id: string, offerUiType: string, offerUiSubType?: string) => void;
  onDuplicate: (
    id: string,
    offerUiType: string,
    offerUiSubType?: string
  ) => void;
  onDelete: (id: string) => void;
}

export interface OfferDesignPermissions {
  canUserEdit: boolean;
  canManage: boolean;
}

export interface FilterCriteria {
  externalId: string;
  name: string;
  types: string[];
}

export interface OfferTypeCardConfig {
  key: string;
  title: string;
  description: string;
  image: string;
  offerDesignType: OfferDesignTypes;
  disabled?: boolean;
  featureFlag?: EFeatureFlag;
  flagTitle?: string;
  flagTitleFlag?: EFeatureFlag;
}

export interface GradientColor {
  colorOne: string;
  colorTwo?: string;
  gradientDirection?: GradientDirection;
}

interface IBaseFormValues {
  name: string;
  externalId: string;
  backgroundImage: string;
  backgroundColor: GradientColor | string;
  buttonSuffixImage: string;
  buttonSuffixAnimation: string;
  borderColor: GradientColor;
  borderWidth: number;
  offerUiType: OfferDesignTypes;
}

interface IPromotionFormValues {
  collectText?: string;
  title?: string;
  fontSize?: number;
  fontWeight?: string;
  fontColor?: GradientColor;
  subTitleText?: string;
  subTitleFontSize?: number;
  subTitleFontWeight?: string;
  subTitleFontColor?: GradientColor;
}

interface IProgressBarFormValues {
  progressBarType?: EProgressBarType;
  backgroundOpacity?: number;
  frameRadius?: number;
  barFillColor?: GradientColor;
  barBackgroundColor?: GradientColor;
  barRadius?: number;
  accumulatedItemImageUrl?: string;
  accumulatedItemBackgroundImageUrl?: string;
  textColor?: string;
  barBackgroundImage?: string;
  headerImage?: string;
  collectedIconImage?: string;
  tooltipBackgroundImage?: string;
  title?: string;
  fontSize?: number;
  fontWeight?: string;
  subTitleText?: string;
  subTitleFontSize?: number;
  subTitleFontWeight?: string;
}

interface IPopupFormValues {
  badgeCoverImage?: string;
  offerUiSubType?: string;
  products?: ProductData[];
}

interface ISubOfferFormValues {
  offersBackgroundColor?: GradientColor;
  offersBackgroundImage?: string;
  offersLockImage?: string;
}

interface IRollingOfferFormValues extends ISubOfferFormValues {
  arrowColor?: string;
  arrowImage?: string;
  enableScroll?: boolean;
  scrollArrowImage?: string;
  horizontalBackgroundImage?: string;
}

interface IBuyOneGetManyFormValues extends ISubOfferFormValues {
  plusImage?: string;
  freeTilesLayout?: number;
  claimedIconImage?: string;
  freeTilesBackgroundImage?: string;
}

interface IRewardCalendarFormValues {
  backgroundColor?: string;
  dayFontColor?: string;
  headerImage?: string;
  collectibleBadgeImage?: string;
  lockedBadgeImage?: string;
  subRewardBackgroundImage?: string;
  mainRewardBackgroundImage?: string;
  collectIconImage?: string;
  lockedIconImage?: string;
}

export type OfferDesignFormValues = IBaseFormValues &
  IPromotionFormValues &
  IProgressBarFormValues &
  IPopupFormValues &
  IRollingOfferFormValues &
  IBuyOneGetManyFormValues &
  IRewardCalendarFormValues;

export type InitialValuesBuilder = (
  data: OfferUI | undefined,
  offerSubType?: string
) => Partial<OfferDesignFormValues>;

export type RenderCardFn = (
  values: OfferDesignFormValues,
  generalTheme: GeneralThemeFormValues,
  storeScreen: StoreThemeFormValues
) => React.ReactNode;

export interface SaveValidationCheck {
  failed: boolean;
  message: string;
  description: string;
  fieldKey?: string;
}

export interface OfferTypeFormConfig {
  FormFields: React.FC<{
    shouldDisplaySubTitle?: boolean;
    specialOffersInternalViewModel?: ESpecialOfferInternalViewModel;
  }>;
  schema: AnyObjectSchema;
  buildPayload: (
    formValues: OfferDesignFormValues,
    offerType: OfferDesignTypes
  ) => OfferUI;
}
